import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { Button } from 'sharedUi/Button';
import pushNotificationService from '../../services/push-notification.service';
import { firebaseConfig } from '../../lib/firebase';
import { getContactId } from '../../utils/current-user';

/**
 * Mandatory web-push permission gate.
 *
 * Browsers only reliably show the permission prompt from a user gesture, so we
 * present a blocking dialog after login and trigger the prompt from a button
 * click. It stays until permission is granted:
 *   - "default" → ask (Enable button → subscribe()).
 *   - "denied"  → the browser won't re-prompt; show unblock steps + Re-check.
 *   - "granted" → gate closes (and the token is registered).
 *
 * Only shown when push is supported AND Firebase is configured — otherwise there
 * is nothing to enable.
 */
const PushPermissionGate = () => {
  const user = useSelector((state: any) => state.auth?.user);
  const isAuthenticated = useSelector((state: any) => state.auth?.isAuthenticated);

  const contactId = getContactId(user);

  const supported = pushNotificationService.isSupported();
  const configured = !!firebaseConfig.apiKey;

  const [permission, setPermission] = useState<NotificationPermission>(
    pushNotificationService.getPermissionStatus(),
  );
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  const recheck = useCallback(() => {
    const p = pushNotificationService.getPermissionStatus();
    setPermission(p);
    return p;
  }, []);

  // Decide whether the gate should be open.
  useEffect(() => {
    if (!isAuthenticated || !user || !supported || !configured) {
      setVisible(false);
      return;
    }
    setVisible(recheck() !== 'granted');
  }, [isAuthenticated, user, supported, configured, recheck]);

  // If the user changes browser settings in another tab, catch it on focus.
  useEffect(() => {
    const onFocus = () => {
      if (recheck() === 'granted') setVisible(false);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [recheck]);

  const enable = async () => {
    setBusy(true);
    try {
      await pushNotificationService.subscribe(contactId); // click → real prompt
      setPermission('granted');
      setVisible(false);
    } catch {
      recheck(); // likely became "denied" or was dismissed; keep the gate open
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  const denied = permission === 'denied';

  return (
    <Dialog
      visible={visible}
      onHide={() => {}}
      closable={false}
      closeOnEscape={false}
      dismissableMask={false}
      header={
        <div className="flex items-center gap-2">
          <i className="pi pi-bell text-indigo-500" />
          <span>Enable notifications</span>
        </div>
      }
      style={{ width: '440px', maxWidth: '92vw' }}
    >
      {!denied ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This app needs permission to send you notifications so you don’t miss
            alerts and important updates. Click below and choose <b>Allow</b> in
            the browser prompt.
          </p>
          <Button
            label={busy ? 'Waiting for permission…' : 'Enable notifications'}
            icon="pi pi-bell"
            className="w-full"
            onClick={enable}
            loading={busy}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Notifications are currently <b className="text-red-600">blocked</b> for
            this site, so the browser won’t ask again. To turn them on:
          </p>
          <ol className="text-sm text-gray-600 dark:text-gray-300 list-decimal ml-5 space-y-1">
            <li>Click the <b>lock / site-info icon</b> in the address bar.</li>
            <li>Find <b>Notifications</b> and set it to <b>Allow</b>.</li>
            <li>Reload the page, then click <b>Re-check</b> below.</li>
          </ol>
          <Button
            label="Re-check"
            icon="pi pi-refresh"
            outlined
            className="w-full"
            onClick={() => {
              if (recheck() !== 'granted') void enable();
            }}
          />
        </div>
      )}
    </Dialog>
  );
};

export default PushPermissionGate;
